/**
 * The booking page's only network dependency.
 *
 * The page moved to the marketing site but the API did not. FRIDAY is the
 * internal business system and it stays that way, so the calendar, the slot
 * tokens and the CRM write all remain behind `friday-api`, and this file is
 * the one seam between the two. It is a plain `fetch` on purpose: nothing here
 * is authenticated, so there is no session, no bearer and no refresh to carry.
 */

/** Where the public booking endpoints live. Overridable for local work. */
export const API_BASE: string = (
  import.meta.env.VITE_FRIDAY_API_BASE ?? 'https://friday-api.innovatex-solutions.workers.dev'
).replace(/\/+$/, '');

export class ApiError extends Error {
  readonly status: number;

  /**
   * A booking the refusal is about, when the API named one.
   *
   * Only "you already have one of these booked" carries it. A refusal that tells
   * somebody they are already booked and gives them nowhere to go is a dead end, so the
   * API hands back the page for the booking they hold and the message becomes a link.
   */
  readonly bookingUrl: string | null;

  constructor(status: number, message: string, bookingUrl: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.bookingUrl = bookingUrl;
  }
}

/**
 * What a visitor reads when the API gives us nothing readable.
 *
 * The API writes its own friendly copy for the cases it knows about, and we
 * prefer that every time. These are the fallbacks for the cases it does not,
 * and none of them says "500" or "fetch failed" out loud.
 */
const FALLBACK = {
  offline: "We couldn't reach the calendar. Check your connection and try again.",
  notFound: "That booking page doesn't exist. Check the link you were given.",
  client: "That didn't go through. Have another look at your details and try again.",
  server: 'Something went wrong at our end. Give it a moment and try again.',
} as const;

function fallbackFor(status: number): string {
  if (status === 404) return FALLBACK.notFound;
  if (status >= 400 && status < 500) return FALLBACK.client;
  return FALLBACK.server;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, init);
  } catch {
    // DNS, offline, CORS. The visitor gets one sentence and something to do.
    throw new ApiError(0, FALLBACK.offline);
  }

  const raw = await response.text();
  let body: unknown = null;
  if (raw) {
    try {
      body = JSON.parse(raw);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const shape = typeof body === 'object' && body !== null
      ? body as { error?: unknown; booking_url?: unknown }
      : {};
    const message = typeof shape.error === 'string' ? shape.error : fallbackFor(response.status);
    const bookingUrl = typeof shape.booking_url === 'string' ? shape.booking_url : null;
    throw new ApiError(response.status, message, bookingUrl);
  }

  return body as T;
}

export const api = {
  /**
   * No `Content-Type` here, and that is deliberate rather than an omission.
   *
   * A GET carrying `Content-Type: application/json` stops being a CORS simple
   * request, which makes the browser send a preflight first. The public GET
   * responses already answer `Access-Control-Allow-Origin: *`, so without the
   * header they work from this origin today; with it, they would need the
   * marketing site's origin on the API's preflight allowlist. A GET has no body
   * to describe anyway.
   */
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),

  /**
   * A POST with a JSON body cannot avoid the preflight, and should not try to.
   * Sending the body as text/plain to dodge it would be a lie about what is on
   * the wire. This one needs the origin on the API's allowlist.
   */
  post: <T>(path: string, payload: unknown) => request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload ?? {}),
  }),
};
